import { Loader } from "@circle-react-uikit/Loader";
import { Typography } from "@circle-react-uikit/Typography";
import type {
  FlaggedContentReport,
  FlaggedContentableType,
} from "@circle-react/types";
import InfiniteScroll from "react-infinite-scroll-component";

import { t } from "@/i18n-js/instance";
import {
  CHAT_ROOM_MESSAGE_TYPE,
  CONNECT_CONNECTION_TYPE,
} from "@/react/types/FlaggedContent";

import { ReportItem } from "./ReportItem";

export interface ReportsListProps {
  currentReportFallback?: FlaggedContentReport;
  currentReportId?: number;
  hasNextPage?: boolean;
  isLoading?: boolean;
  onLoadMore?: () => void;
  reports?: FlaggedContentReport[];
  type?: FlaggedContentableType;
}

const i18nRoot = "settings.moderation.review_flagged_content_modal";
const SCROLLABLE_TARGET_ID = "reports-list-scroll-container";

const renderReportSection = (title: string, items: FlaggedContentReport[]) =>
  items.length > 0 && (
    <div className="flex flex-col gap-6">
      <Typography.LabelXs color="text-light" weight="semibold">
        {title}
      </Typography.LabelXs>
      <div className="flex flex-col gap-6">
        {items.map(report => (
          <ReportItem
            key={report.id}
            {...report}
            showStatusBadge
            status={report.status}
          />
        ))}
      </div>
    </div>
  );

export const ReportsList = ({
  currentReportFallback,
  currentReportId,
  hasNextPage = false,
  isLoading = false,
  onLoadMore,
  reports = [],
  type,
}: ReportsListProps) => {
  if (isLoading && reports.length === 0) {
    return (
      <div className="flex items-center justify-center px-8 py-12">
        <Loader center />
      </div>
    );
  }

  if (reports.length === 0) {
    return (
      <div className="flex flex-col gap-6 px-8 py-6">
        <Typography.LabelSm color="text-lightest">
          {t([i18nRoot, "empty"])}
        </Typography.LabelSm>
      </div>
    );
  }

  const shouldUseThisReportOtherReportsLayout =
    type === CONNECT_CONNECTION_TYPE || type === CHAT_ROOM_MESSAGE_TYPE;

  if (shouldUseThisReportOtherReportsLayout) {
    const currentReport =
      reports.find(r => r.id === currentReportId) ?? currentReportFallback;
    const otherReports = reports.filter(r => r.id !== currentReportId);

    return (
      <div
        id={SCROLLABLE_TARGET_ID}
        className="flex max-h-96 flex-col gap-6 overflow-y-auto px-8 py-6"
      >
        {renderReportSection(
          t([i18nRoot, "this_report"]),
          currentReport ? [currentReport] : [],
        )}
        <InfiniteScroll
          style={{ overflow: "initial" }}
          scrollThreshold={0.8}
          next={() => onLoadMore?.()}
          hasMore={hasNextPage}
          loader={<Loader center />}
          dataLength={otherReports.length}
          scrollableTarget={SCROLLABLE_TARGET_ID}
        >
          {renderReportSection(t([i18nRoot, "other_reports"]), otherReports)}
        </InfiniteScroll>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 px-8 py-6">
      {reports.map(report => (
        <ReportItem key={report.id} {...report} />
      ))}
    </div>
  );
};
